import { AttributeDescriptor } from "./schema";
import { assertUnreachable } from "./tsHelper";

export type ConvertedAttribute = boolean | string | unknown[] | number | object | undefined;

export function convertAttribute(
  schema: AttributeDescriptor,
  value: string | undefined
): ConvertedAttribute | undefined {
  switch (schema.type) {
    case "boolean":
      return convertToBoolean(value, schema.default);
    case "string":
    case "guiStyle":
    case "cameraType":
    case "videoFit":
    case "codeDirection":
      return convertToString(value, schema.default);
    case "array":
      return convertToArray(value, schema.default);
    case "integer":
      return convertToInteger(value, schema.default);
    case "singleImageModeSettings":
    case "searchArea":
    case "camera":
    case "cameraSettings":
      return convertJsonToObject(value, schema.default);
    default:
      break;
  }

  // Trick to make sure all cases are covered:
  return assertUnreachable(schema);
}

function convertToBoolean(input: string | undefined, defaultValue: boolean): boolean {
  return input == null ? defaultValue : input !== "false";
}

function convertToString(input: string | undefined, defaultValue: string | undefined): string | undefined {
  return input ?? defaultValue;
}

function convertToInteger(input: string | undefined, defaultValue: number | undefined): number | undefined {
  if (input == null) {
    return defaultValue;
  }
  const parsed: number = parseInt(input, 10);
  if (isNaN(parsed)) {
    return defaultValue;
  }

  return parsed;
}

function convertToArray(input: string | undefined, defaultValue: unknown[] | undefined): unknown[] | undefined {
  if (input == null) {
    return defaultValue;
  }

  const json: JSON | undefined = toJson(input);
  if (json == null || !Array.isArray(json)) {
    return defaultValue;
  }

  return json;
}

function convertJsonToObject(input: string | undefined, defaultValue: object | undefined): object | undefined {
  if (input == null) {
    return defaultValue;
  }
  const json: JSON | undefined = toJson(input);
  if (json == null) {
    return defaultValue;
  }
  // must be an object
  if (Array.isArray(json) || typeof json === "number") {
    return defaultValue;
  }

  return json;
}

function toJson(input: string): JSON | undefined {
  try {
    return JSON.parse(input);
  } catch (e) {
    return;
  }
}
